import { useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';
import { useStripe } from '@stripe/react-stripe-js';

import { sendFEEvent } from '../../../../../eventUtil';
import { useAPI } from '../../../../../services/api.service';
import { useCheckout } from '../../../../../hooks/use-checkout';
import { SavedCardFormProps } from '../../SavedCards/SavedCards';
import { useOrderPolling } from '../../../hooks/use-order-polling';
import { usePaymentState } from '../../../hooks/use-payment-state';
import { createStripeSavedCardFinalizeRequestBody } from '../utils';
import {
  ESdkEvents,
  EventParams,
  StripeFinalizePaymentResponse,
  AppchargeErrorType,
} from '../../../../../contracts';
import { generateAppchargeErrorMessage } from '../../../../../utils/generate-appcharge-error-message';

export function useStripeSavedCardSubmit() {
  const { checkoutData, selectedPaymentMethod, getEventParams } = useCheckout();
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const stripe = useStripe();
  const { API } = useAPI();
  const { startOrderPolling } = useOrderPolling();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { showBoundary } = useErrorBoundary();

  const isStripePaymentInProgress = useRef(false);

  const handleSubmit = async (formValues: SavedCardFormProps) => {
    if (isStripePaymentInProgress.current) {
      return;
    }
    isStripePaymentInProgress.current = true;
    setIsPaymentLoading(true);
    const FEeventParams: Partial<EventParams> = {
      isSavedPaymentMethodUsed: true,
    };
    try {
      const body = createStripeSavedCardFinalizeRequestBody(formValues, selectedPaymentMethod!);
      const response = await API.finalizePayment<StripeFinalizePaymentResponse>(body);
      await handleServerResponse(response, FEeventParams);
    } catch (error: any) {
      const message = error?.response?.data?.message;
      showBoundary(t('unableToProcessPayment'));
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: message,
        ...FEeventParams,
        error: {
          isFatal: true,
          reason: message,
        },
      });
      handleError();
    }
  };

  async function handleServerResponse(
    response: StripeFinalizePaymentResponse,
    FEeventParams: Partial<EventParams>
  ) {
    if (response.stripe.status === 'succeeded') {
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
        ...getEventParams(),
        ...FEeventParams,
      });
      startOrderPolling(response.orderId, FEeventParams);
    } else if (response.stripe.status === 'failed') {
      handleError(response.appChargeErrorType);
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: response.stripe.message || '',
      });
    } else if (response.stripe.status === 'redirect') {
      const paymentIntentResult = await stripe?.handleNextAction({
        clientSecret: response.stripe.clientSecret,
      });
      if (paymentIntentResult?.error) {
        const errorMessage = paymentIntentResult.error.message || '';
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
          ...getEventParams(),
          ...FEeventParams,
          reason: errorMessage,
        });
        handleError();
      } else if (
        paymentIntentResult?.paymentIntent?.status === 'requires_payment_method' ||
        paymentIntentResult?.paymentIntent?.status === 'requires_action'
      ) {
        const errorMessage = paymentIntentResult?.paymentIntent?.last_payment_error?.message || '';
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
          ...getEventParams(),
          ...FEeventParams,
          reason: errorMessage,
        });
        handleError(errorMessage as any);
      } else {
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
          ...getEventParams(),
          ...FEeventParams,
        });
        startOrderPolling(response.orderId, FEeventParams);
      }
    }
  }

  function handleError(appChargeErrorType?: AppchargeErrorType) {
    const errorMessage = generateAppchargeErrorMessage(t, appChargeErrorType);
    setErrorMessage(errorMessage);
    setIsPaymentLoading(false);
    isStripePaymentInProgress.current = false;
  }

  return { handleSubmit };
}
